import { useEffect, useMemo, useRef, useState, useTransition } from 'react';
import * as THREE from 'three';
import type { NavMeshHelper } from '@slope/navmesh/three';
import { buildCameraTravelPath } from '@slope/r3f';

type CacheBlob = {
  v: 1; // version for future migrations
  seg: number; // number of segments sampled
  pts: [number, number, number][]; // sampled points
};

type Params = {
  modelUrl: string;
  navMesh: NavMeshHelper | null;
  height: number;
  distance: number;
  // optional knobs to influence cache key granularity:
  segments?: number; // default 200
  extraKey?: string; // e.g., selectedHole or course transform hash if relevant
};

const LS_PREFIX = 'cameraPath:';
const CURVE_CLOSED = false; // keep open unless you want loops
const CURVE_TYPE: THREE.CurveType = 'centripetal';

function makeKey(modelUrl: string) {
  return `${LS_PREFIX}${modelUrl}`;
}

function serializeCurve(curve: THREE.Curve<THREE.Vector3>, segments: number): CacheBlob {
  const pts = curve.getPoints(segments).map((v) => [v.x, v.y, v.z] as [number, number, number]);
  return { v: 1, seg: segments, pts };
}

function deserializeCurve(blob: CacheBlob): THREE.Curve<THREE.Vector3> | null {
  if (!blob || blob.v !== 1 || !Array.isArray(blob.pts)) return null;
  const vecs = blob.pts.map(([x, y, z]) => new THREE.Vector3(x, y, z));
  // Reconstruct as CatmullRom – good fidelity for camera rails
  return new THREE.CatmullRomCurve3(vecs, CURVE_CLOSED, CURVE_TYPE);
}

function readCache(key: string): THREE.Curve<THREE.Vector3> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const blob = JSON.parse(raw) as CacheBlob;
    return deserializeCurve(blob);
  } catch {
    return null;
  }
}

function writeCache(key: string, curve: THREE.Curve<THREE.Vector3>, segments: number) {
  try {
    const blob = serializeCurve(curve, segments);
    localStorage.setItem(key, JSON.stringify(blob));
  } catch {
    // ignore quota or serialization errors
  }
}

export function useCachedCameraPath({
  modelUrl,
  navMesh,
  height,
  distance,
  segments = 200,
}: Params) {
  const [cameraPath, setCameraPath] = useState<THREE.Curve<THREE.Vector3> | null>(null);
  const [pending, startTransition] = useTransition();
  const key = useMemo(() => makeKey(modelUrl), [modelUrl]);

  // prevent overlapping computations if deps thrash
  const computingRef = useRef<Promise<THREE.Curve<THREE.Vector3> | null> | null>(null);

  // Try cache immediately when deps change
  useEffect(() => {
    const cached = readCache(key);
    if (cached) {
      setCameraPath(cached);
    } else {
      setCameraPath(null);
    }
  }, [key]);

  // Compute (and cache) if needed
  useEffect(() => {
    if (!navMesh) {
      setCameraPath(null);
      computingRef.current = null;
      return;
    }
    // If we already have a cached path, skip compute
    if (cameraPath) return;

    const compute = async () => {
      try {
        const path = await Promise.resolve(buildCameraTravelPath(navMesh, height, distance));
        if (!path) return null;
        // cache it
        writeCache(key, path, segments);
        return path;
      } catch {
        return null;
      }
    };

    if (!computingRef.current) {
      startTransition(() => {
        computingRef.current = compute().then((result) => {
          setCameraPath(result);
          computingRef.current = null;
          return result;
        });
      });
    }
  }, [navMesh, height, distance, segments, key, cameraPath]);

  const invalidate = () => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      void e;
    }
    setCameraPath(null);
  };

  return { cameraPath, pending, invalidate, cacheKey: key };
}
